import type { AlternativeKind, AlternativeSuggestion, Availability } from '../core/model/availability';
import { DEFAULT_CANCELLATION_POLICY, type Reservation, type ReservationStatus } from '../core/model/reservation';
import { addressSingleLine, hasCoordinates } from '../core/model/restaurant';
import type {
  Address,
  OpeningHours,
  Promotion,
  Restaurant,
  Review,
  TimeSlot,
} from '../core/model/restaurant';
import type { UserProfile } from '../core/model/search';
import type { DayOfWeek, LocalDate, LocalDateTime, LocalTime } from '../core/util/datetime';
import { compare, isValidDate, isValidTime } from '../core/util/datetime';
import { Formatters } from '../core/util/formatters';
import type {
  AddressDto,
  AlternativeDto,
  AvailabilityDto,
  OpeningHoursDto,
  PromotionDto,
  ReservationDto,
  RestaurantDto,
  ReviewDto,
  TimeSlotDto,
  UserDto,
} from './dto';

/**
 * DTO → dominio.
 *
 * Reglas transversales:
 * 1. Los textos pasan por `Formatters.sanitize`, que convierte "", "null", "N/A"… en `null`, de
 *    modo que la basura del backend nunca llega a la pantalla.
 * 2. Un valor fuera de rango (rating 7.3, priceLevel 9) se descarta en lugar de propagarse.
 * 3. Una fecha/hora ilegible no lanza: devuelve `null` y el registro se descarta arriba.
 * 4. Un restaurante sin `name` **no se mapea**: una tarjeta sin nombre no es útil.
 */

function inRange(value: number | null | undefined, min: number, max: number): number | null {
  return value != null && Number.isFinite(value) && value >= min && value <= max ? value : null;
}

export function parseTime(raw: string | null | undefined): LocalTime | null {
  const clean = Formatters.sanitize(raw);
  if (!clean) return null;
  // El servidor puede mandar "20:30" o "20:30:00"; ambas son la misma hora de reloj.
  const trimmed = clean.length > 5 ? clean.slice(0, 5) : clean;
  return isValidTime(trimmed) ? trimmed : null;
}

export function parseDate(raw: string | null | undefined): LocalDate | null {
  const clean = Formatters.sanitize(raw);
  if (!clean) return null;
  return isValidDate(clean) ? clean : null;
}

export function parseDateTime(raw: string | null | undefined): LocalDateTime | null {
  const clean = Formatters.sanitize(raw);
  if (!clean) return null;
  const [datePart, timePart] = clean.split('T');
  const date = parseDate(datePart);
  const time = parseTime(timePart);
  return date && time ? `${date}T${time}` : null;
}

export function addressToDomain(dto: AddressDto | null | undefined): Address | null {
  if (!dto) return null;
  const address: Address = {
    street: Formatters.sanitize(dto.street),
    neighborhood: Formatters.sanitize(dto.neighborhood),
    city: Formatters.sanitize(dto.city),
    latitude: inRange(dto.latitude, -90, 90),
    longitude: inRange(dto.longitude, -180, 180),
  };
  // Una dirección sin ninguna parte legible ni coordenadas no aporta nada: mejor ausente que
  // como un objeto vacío que la UI tendría que volver a inspeccionar.
  return addressSingleLine(address) === null && !hasCoordinates(address) ? null : address;
}

export function openingHoursToDomain(dto: OpeningHoursDto): OpeningHours | null {
  const day = inRange(dto.day_of_week, 1, 7);
  if (day === null || !Number.isInteger(day)) return null;
  const opens = parseTime(dto.opens_at);
  const closes = parseTime(dto.closes_at);
  if (!opens || !closes) return null;
  return { dayOfWeek: day as DayOfWeek, opensAt: opens, closesAt: closes };
}

export function promotionToDomain(dto: PromotionDto | null | undefined): Promotion | null {
  if (!dto) return null;
  const label = Formatters.sanitize(dto.label);
  if (!label) return null;
  return { label, description: Formatters.sanitize(dto.description) };
}

export function timeSlotToDomain(dto: TimeSlotDto): TimeSlot | null {
  const time = parseTime(dto.time);
  if (!time) return null;
  // Sin token no se puede reservar: mostrar el chip sería ofrecer un botón sin acción.
  const token = Formatters.sanitize(dto.token);
  if (!token) return null;
  return {
    time,
    token,
    available: dto.available ?? true,
    label: Formatters.sanitize(dto.label),
  };
}

export function restaurantToDomain(dto: RestaurantDto): Restaurant | null {
  const name = Formatters.sanitize(dto.name);
  if (!name) return null;
  const priceLevel = inRange(dto.price_level, 1, 4);
  return {
    id: dto.id,
    name,
    cuisine: Formatters.sanitize(dto.cuisine),
    priceLevel: priceLevel !== null && Number.isInteger(priceLevel) ? priceLevel : null,
    rating: inRange(dto.rating, 0, 5),
    reviewCount: Math.max(0, dto.review_count ?? 0),
    photos: (dto.photos ?? []).map((p) => Formatters.sanitize(p)).filter((p): p is string => p !== null),
    address: addressToDomain(dto.address),
    distanceMeters: dto.distance_meters != null && dto.distance_meters >= 0 ? dto.distance_meters : null,
    openingHours: (dto.opening_hours ?? [])
      .map(openingHoursToDomain)
      .filter((h): h is OpeningHours => h !== null),
    amenities: (dto.amenities ?? [])
      .map((a) => Formatters.sanitize(a))
      .filter((a): a is string => a !== null),
    phone: Formatters.sanitize(dto.phone),
    menuUrl: Formatters.sanitize(dto.menu_url),
    description: Formatters.sanitize(dto.description),
    promotion: promotionToDomain(dto.promotion),
    isCurrentlyOpen: dto.is_open_now ?? null,
    previewSlots: (dto.preview_slots ?? [])
      .map(timeSlotToDomain)
      .filter((s): s is TimeSlot => s !== null),
  };
}

export function alternativeToDomain(dto: AlternativeDto): AlternativeSuggestion | null {
  const date = parseDate(dto.date);
  const time = parseTime(dto.time);
  if (!date || !time) return null;
  if (dto.party_size == null || dto.party_size <= 0) return null;
  const raw = dto.kind?.toUpperCase();
  const kind: AlternativeKind =
    raw === 'ANOTHER_DAY' || raw === 'SMALLER_PARTY' ? raw : 'NEARBY_TIME';
  return { date, time, partySize: dto.party_size, kind };
}

export function availabilityToDomain(
  dto: AvailabilityDto,
  fallbackId: string,
  fallbackDate: LocalDate,
  fallbackParty: number,
): Availability {
  return {
    restaurantId: Formatters.sanitize(dto.restaurant_id) ?? fallbackId,
    date: parseDate(dto.date) ?? fallbackDate,
    partySize: dto.party_size != null && dto.party_size > 0 ? dto.party_size : fallbackParty,
    slots: (dto.slots ?? [])
      .map(timeSlotToDomain)
      .filter((s): s is TimeSlot => s !== null)
      .sort((a, b) => compare(a.time, b.time)),
    alternatives: (dto.alternatives ?? [])
      .map(alternativeToDomain)
      .filter((a): a is AlternativeSuggestion => a !== null),
    closedThatDay: dto.closed_that_day ?? false,
  };
}

export function reviewToDomain(dto: ReviewDto): Review | null {
  const rating = inRange(dto.rating, 0, 5);
  if (rating === null) return null;
  return {
    id: dto.id,
    authorName: Formatters.sanitize(dto.author_name) ?? 'Comensal verificado',
    rating,
    comment: Formatters.sanitize(dto.comment),
    date: parseDate(dto.date),
    foodRating: inRange(dto.food_rating, 0, 5),
    serviceRating: inRange(dto.service_rating, 0, 5),
    ambienceRating: inRange(dto.ambience_rating, 0, 5),
  };
}

function parseStatus(raw: string | null | undefined): ReservationStatus {
  switch (raw?.toUpperCase()) {
    case 'CANCELLED':
    case 'CANCELED':
      return 'CANCELLED';
    case 'COMPLETED':
      return 'COMPLETED';
    case 'NO_SHOW':
      return 'NO_SHOW';
    default:
      return 'CONFIRMED';
  }
}

export function reservationToDomain(dto: ReservationDto): Reservation | null {
  // Una reserva sin fecha, hora o código no se puede presentar ni usar: se descarta en lugar
  // de mostrarse a medias.
  const date = parseDate(dto.date);
  const time = parseTime(dto.time);
  const code = Formatters.sanitize(dto.confirmation_code);
  const restaurantId = Formatters.sanitize(dto.restaurant_id);
  if (!date || !time || !code || !restaurantId) return null;
  return {
    id: dto.id,
    confirmationCode: code,
    restaurantId,
    restaurantName: Formatters.sanitize(dto.restaurant_name) ?? 'Restaurante',
    restaurantPhoto: Formatters.sanitize(dto.restaurant_photo),
    address: addressToDomain(dto.address),
    date,
    time,
    partySize: Math.max(1, dto.party_size ?? 1),
    status: parseStatus(dto.status),
    guest: {
      fullName: Formatters.sanitize(dto.guest_name) ?? '',
      phone: Formatters.sanitize(dto.guest_phone) ?? '',
      email: Formatters.sanitize(dto.guest_email) ?? '',
    },
    occasion: Formatters.sanitize(dto.occasion),
    notes: Formatters.sanitize(dto.notes),
    createdAt: parseDateTime(dto.created_at),
    cancellationPolicy: {
      freeCancellationHours:
        dto.free_cancellation_hours != null
          ? Math.max(0, dto.free_cancellation_hours)
          : DEFAULT_CANCELLATION_POLICY.freeCancellationHours,
      holdMinutes:
        dto.hold_minutes != null ? Math.max(0, dto.hold_minutes) : DEFAULT_CANCELLATION_POLICY.holdMinutes,
    },
  };
}

export function userToDomain(dto: UserDto): UserProfile {
  const defaultParty = inRange(dto.default_party_size, 1, 20);
  return {
    id: dto.id,
    fullName: Formatters.sanitize(dto.name) ?? '',
    email: Formatters.sanitize(dto.email) ?? '',
    phone: Formatters.sanitize(dto.phone),
    favoriteCuisines: (dto.favorite_cuisines ?? [])
      .map((c) => Formatters.sanitize(c))
      .filter((c): c is string => c !== null),
    defaultPartySize: defaultParty,
  };
}
